"use client";

import {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import type { SessionNotificationState, NotificationAPI } from "@/hooks/use-notifications";
import type { InboxNotificationAPI } from "@/hooks/use-inbox-notifications";

interface NotificationContextValue {
  notificationState: Map<string, SessionNotificationState>;
  markSessionRead: (sessionId: string) => void;
  api: NotificationAPI;
  /** Durable server-side inbox state and actions. */
  inbox: InboxNotificationAPI;
  /**
   * Directly set a session's run status in both localStorage and the
   * in-memory notification state, bypassing the background poll cycle.
   * Called by ChatInterface when the SSE/polling chat status changes so
   * the sidebar indicator reacts immediately.
   */
  setRunStatus: (sessionId: string, status: string) => void;
  /**
   * Records a run whose terminal response was already shown by the live SSE
   * stream. The durable inbox uses this page-lifetime registry to suppress a
   * slower notification row for that same run.
   */
  registerSeenRun: (sessionId: string, runId: string) => void;
}

const NotificationContext = createContext<NotificationContextValue | undefined>(undefined);
const noopRegisterSeenRun = () => {};

export function useNotificationContext(): NotificationContextValue {
  const ctx = useOptionalNotificationContext();
  if (!ctx) {
    throw new Error("useNotificationContext must be used within <NotificationProvider>");
  }
  return ctx;
}

/** Standalone read-only transcripts have no chat notification shell. */
export function useOptionalNotificationContext(): NotificationContextValue | undefined {
  return useContext(NotificationContext);
}

export function NotificationProvider({
  notificationState,
  api,
  inbox,
  markSessionRead,
  setRunStatus,
  registerSeenRun,
  children,
}: {
  notificationState: Map<string, SessionNotificationState>;
  markSessionRead: (sessionId: string) => void;
  api: NotificationAPI;
  inbox: InboxNotificationAPI;
  setRunStatus: (sessionId: string, status: string) => void;
  /** Optional for lightweight consumers and tests outside the app shell. */
  registerSeenRun?: (sessionId: string, runId: string) => void;
  children: ReactNode;
}) {
  const value: NotificationContextValue = useMemo(() => ({
    notificationState,
    markSessionRead,
    api,
    inbox,
    setRunStatus,
    registerSeenRun: registerSeenRun ?? noopRegisterSeenRun,
  }), [notificationState, markSessionRead, api, inbox, setRunStatus, registerSeenRun]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}
